import { Children, FC, ReactNode, useEffect, useRef, useState, RefObject, MutableRefObject, useMemo, isValidElement, cloneElement, type JSX } from "react";
import styles from "./Module.module.scss";
import { classNames, isMobileSafari } from "@/lib/utils";
import { backgroundColorState, themeState, userDataState, textColorState, greyDarkerState, ortexColorState } from "@/lib/store";
import { useAtomValue } from "jotai";
import dynamic from "next/dynamic";
import { getModuleHelp, upsertModuleHelp } from "@/controllers/module-help";
import Link from "next/link";
import { useFullscreen, useToggle } from "react-use";
import { ModuleContext } from "@/lib/Context";
import Image from "next/legacy/image";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { Breakpoint, Button, CardHeader, Typography, useMediaQuery } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FullscreenOutlinedIcon from "@mui/icons-material/FullscreenOutlined";
import CloseFullscreenOutlinedIcon from "@mui/icons-material/CloseFullscreenOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import IosShareOutlinedIcon from "@mui/icons-material/IosShareOutlined";
import FiltersBtn from "../filters-btn/FiltersBtn";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Tooltip from "@mui/material/Tooltip";
import { NoPaddingType } from "@/models/module";
import StableErrorBoundary from "@/components/error-boundary/StableErrorBoundary";
import ModuleErrorFallback from "../error-fallback/ModuleErrorFallback";
import { inlineFaIcons } from "@/components/fa-icon/FaIcon";
import { useHydrated } from "src/hooks/useHydrated";
// The help and settings dialogs mount on first open (useOpenedOnce), so their code (with DOMPurify and the drag
// handle) loads then too, not with the shell on every page (ortex-gui#1285).
const DialogBasic = dynamic(() => import("../dialog/Dialog"), { ssr: false });
// Staff-only help editor: loaded on first open so it stays out of the shared _app bundle.
const RichTextEditor = dynamic(() => import("@/components/rich-text-editor/RichTextEditor"), { ssr: false });
// The feedback form renders once a reader has opened it (feedbackOpened), so its code waits for that too.
const FeedbackForm = dynamic(() => import("../feedback-form/FeedbackForm"), { ssr: false });

export type ModuleModalProps = {
    component?: ReactNode;
    width?: false | Breakpoint;
    extraButtons?: ReactNode;
    modalCloseOnExtraClick?: boolean;
};

type PropsTypes = {
    children?: ReactNode;
    help?: {
        moduleId: string;
        modulePage: string;
    };
    href?: string;
    logoUrl?: string;
    subHeader?: ReactNode;
    modal?: ModuleModalProps;
    settingsComponent?: ReactNode;
    settingsComponentCentered?: ReactNode;
    title?: JSX.Element | string;
    /**
     * Controls that belong next to the title (an index picker, a chip, a button), drawn after the
     * heading instead of inside it: a heading holds its text only, so search engines and screen
     * readers read "Market Sentiment" rather than the select's markup and styles.
     */
    titleAside?: ReactNode;
    /** The title's element when it must differ from its look: "h1" for a page's one heading drawn as a module title. */
    titleAs?: "h1" | "h2";
    allowFullscreen?: boolean;
    obfuscated?: boolean;
    noHeader?: boolean;
    noPadding?: NoPaddingType;
    noBackground?: boolean;
    filters?: JSX.Element;
    /** Kept for callers that still pass it; chart sonification ended with the previous chart engine. */
    sonification?: MutableRefObject<{
        chart: unknown;
        container: RefObject<HTMLDivElement>;
    }>;
    link?: string;
    headerButton?: { content: string | ReactNode; onClick: () => void; variant?: "outlined" | "text" | "contained" };
    /** Extra action buttons at the top of the help dialog — e.g. "Restart
     *  the guided tour". Rendered only when the dialog is open. */
    helpActions?: { label: string; onClick: () => void }[];
    contentLayout?: boolean;
    stockPage?: boolean;
    enableFilterProp?: boolean;
    /**
     * The module sits in a page that scrolls as a whole (Earnings, Buybacks), so its
     * content must not be a scroll container. By default the content is `overflow: auto`
     * with `overscroll-behavior: contain` for the fixed-height layouts where the module
     * is the scroller; in a scrolling page that same rule traps every finger drag and
     * wheel that starts inside the module - the container cannot scroll, chaining stops
     * there, and the page never moves while the scrollbar still works (Peter on a Pixel,
     * 2026-09-28).
     */
    scrollsWithPage?: boolean;
    feedback?: boolean;
    /** Renders a share icon in the header (next to feedback/help) wired to `onClick`. */
    share?: { onClick: () => void; tooltip?: string };
};

/** True from the first time `open` is true. The module's dialogs mount then and stay mounted, so their
 * open and close transitions are unchanged, but a page of modules no longer renders two or three closed
 * dialogs each during start-up. */
const useOpenedOnce = (open: boolean): boolean => {
    const opened = useRef(false);
    if (open) opened.current = true;
    return opened.current;
};

const Module: FC<PropsTypes> = ({
    children,
    feedback = false,
    share,
    help,
    href,
    modal: { component: modalComponent, width: modalComponentWitdh, extraButtons: modalExtraButtons, modalCloseOnExtraClick } = {},
    logoUrl,
    subHeader,
    settingsComponent,
    settingsComponentCentered,
    title,
    titleAside,
    titleAs,
    allowFullscreen,
    obfuscated: parentObfuscated,
    noHeader,
    noPadding,
    noBackground,
    filters,
    sonification,
    link,
    headerButton,
    helpActions,
    contentLayout,
    stockPage,
    enableFilterProp,
    scrollsWithPage,
}) => {
    const theme = useAtomValue(themeState);
    const textColor = useAtomValue(textColorState);
    const userData = useAtomValue(userDataState);
    const backgroundColor = useAtomValue(backgroundColorState);
    const greyDarker = useAtomValue(greyDarkerState);
    const ortexColor = useAtomValue(ortexColorState);

    // A stock page's header title is a node carrying its own <h1> (the stock name, RootLayout),
    // so the wrapper must not be a heading too: nested <h1>s gave every stock page two.
    const titleComponent = stockPage && typeof title !== "string" ? "div" : titleAs;

    // The server cannot tell an iPhone, so the fullscreen button is hidden there only once hydrated.
    const hydrated = useHydrated();
    const [canOpen, setCanOpen] = useState(true);
    const [helpModal, setHelpModal] = useState(false);
    const [feedbackModal, setFeedbackModal] = useState(false);
    const [obfuscated, setObfuscated] = useState(false);
    const [settingsModal, setSettingsModal] = useState(false);
    const helpOpened = useOpenedOnce(helpModal);
    const settingsOpened = useOpenedOnce(settingsModal);
    const feedbackOpened = useOpenedOnce(feedbackModal);
    const [content, setContent] = useState("No help found.");
    const [filteredAmount, setFilterAmount] = useState<number>(0);
    const [fullscreen, toggleFullscreen] = useToggle(false);

    // Drives both the (i) icon and the dialog body: a module with no help of
    // its own still shows the icon when it has an action to offer.
    const hasHelpActions = Boolean(helpActions && helpActions.length > 0);

    const moduleRef = useRef(null);

    const isFullscreen = useFullscreen(moduleRef, fullscreen, { onClose: () => toggleFullscreen(false) });

    const isMobile = useMediaQuery("(max-width: 769px)");
    const isTablet = useMediaQuery("(max-width: 1280px)");
    const zeroPadding = noPadding?.type === "all" || (noPadding?.type === "desktop" && !isMobile) || (noPadding?.type === "mobile" && isMobile) || (noPadding?.type === "tablet" && isTablet);

    const getHelp = () => {
        // The dialog can be opened for the tour action alone, on a page that
        // has no module help written for it — open straight onto the actions
        // rather than fetching help that does not exist.
        if (!help) {
            setHelpModal(true);
            return;
        }
        setCanOpen(false);
        getModuleHelp(help.moduleId, help.modulePage)
            // A module whose help has never been written answers 404 with an
            // empty body, which `validateStatus: status < 500` resolves rather
            // than throws. Overwriting with "" would blank the modal, so keep
            // the "No help found." placeholder instead.
            .then((data) => data && setContent(data))
            .finally(() => {
                setCanOpen(true);
                setHelpModal(true);
            });
    };

    const saveHelp = (markup: string) => process.env.NEXT_PUBLIC_VERCEL_ENV && upsertModuleHelp(help.moduleId, help.modulePage, markup);

    const [module_header_height, setModule_header_height] = useState(0);
    const module_header_Ref = useRef(null);

    // Whether the content actually overflows its box. The content is `overflow: auto`
    // for the fixed-height layouts where the module is the scroller; but a box that is
    // `overflow: auto` and does NOT overflow still swallows every drag and wheel that
    // starts inside it, and the page behind it never moves (Peter on a Pixel,
    // 2026-09-28: Earnings and Buybacks, and the same on the stock page in the
    // browser pane). So the content is a scroll container only while it overflows,
    // measured with a ResizeObserver on the box and on what is inside it.
    const contentRef = useRef<HTMLDivElement>(null);
    const [contentOverflows, setContentOverflows] = useState(true);
    useEffect(() => {
        const box = contentRef.current;
        if (!box || typeof ResizeObserver === "undefined") return;
        const measure = () => setContentOverflows(box.scrollHeight > box.clientHeight + 1);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(box);
        if (box.firstElementChild) observer.observe(box.firstElementChild);
        return () => observer.disconnect();
    }, []);

    const modalExtraButtonsWithClose = modalExtraButtons
        ? Children.map(modalExtraButtons as ReactNode, (child) =>
              isValidElement(child)
                  ? // clone each element to wrap its onClick and close the settings modal afterwards
                    cloneElement(child as any, {
                        onClick: (e: any) => {
                            // call original handler if present
                            (child as any).props.onClick && (child as any).props.onClick(e);
                            // close settings modal
                            setSettingsModal(false);
                        },
                    })
                  : child
          )
        : null;

    useEffect(() => {
        if (!noHeader) setModule_header_height(module_header_Ref.current.clientHeight);
    }, []);

    useEffect(() => setObfuscated(parentObfuscated), [parentObfuscated]);

    // A new value on every render made every module look updated to React 18 while the page was
    // hydrating, which threw the server HTML away (React #421); it changes only with its contents now.
    const moduleContext = useMemo(
        () => ({ settings: { moduleId: help?.moduleId, modulePage: help?.modulePage }, isFullscreen, obfuscated, setObfuscated, filteredAmount, setFilterAmount }),
        [help?.moduleId, help?.modulePage, isFullscreen, obfuscated, filteredAmount]
    );

    return (
        <ModuleContext.Provider value={moduleContext}>
            <StableErrorBoundary
                fallbackRender={() => <ModuleErrorFallback />}
                onError={(error: Error, info: { componentStack: string }) => {
                    console.error(error, info);
                }}
            >
                <div className={classNames(styles.Module, styles[theme], contentLayout && styles.pageWrapper, contentLayout && stockPage && styles.stockHeader)} ref={moduleRef}>
                    <Card variant={theme === "light" || contentLayout ? "elevation" : "outlined"} sx={{ height: "100%", backgroundColor: "transparent", borderColor: "rgb(62, 62, 62)" }} elevation={theme === "light" && !contentLayout ? 1 : 0}>
                        {!noHeader && (
                            <CardHeader
                                ref={module_header_Ref}
                                className={classNames(styles["title-container"], stockPage && styles["no-padding-bottom"])}
                                style={{
                                    borderBottom: !contentLayout && theme === "dark" ? `1px solid ${greyDarker}` : "0px solid transparent",
                                    boxShadow: contentLayout && theme === "light" ? "none" : theme === "light" ? "rgba(49, 49, 164, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px" : undefined,
                                    padding: 0,
                                    /*  margin: (title || settingsComponent || allowFullscreen || modalComponent) && 5 */
                                }}
                                sx={{
                                    backgroundColor: !contentLayout || noBackground ? backgroundColor : "transparent",
                                    // A page's title row keeps to the card's width (the title gives way, with an
                                    // ellipsis); MUI's content box otherwise grew to the unwrapped title. The page frame
                                    // does not clip, so on a phone that made the app's scroller pan sideways.
                                    ...((contentLayout || stockPage) && { "& .MuiCardHeader-content": { minWidth: 0 } }),
                                }}
                                title={
                                    title && (
                                        <>
                                            <Box className={styles.title_grid} sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                                                {/* Left: Title.  On stock-pages the title slot takes
                                                    every pixel the action icons do not, and the
                                                    inner heading is widened to match, so children of
                                                    <StockReportPage/>'s custom title (e.g.
                                                    <StockReportBadge/>) can right-align against the
                                                    row edge. It used to be forced to the full row
                                                    width (minWidth: 100%), which pushed the Help icon
                                                    after it off the edge of the card and gave every
                                                    stock page a horizontal scrollbar. */}
                                                <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, flex: stockPage ? "1 1 auto" : "0 1 auto" }}>
                                                    {href ? (
                                                        <Link passHref legacyBehavior href={href}>
                                                            <Typography variant={contentLayout ? "h1" : "h2"} component={titleComponent} style={{ color: textColor }} noWrap sx={stockPage ? { width: "100%" } : undefined}>
                                                                {title}
                                                            </Typography>
                                                        </Link>
                                                    ) : (
                                                        <Typography variant={contentLayout ? "h1" : "h2"} component={titleComponent} noWrap sx={stockPage ? { width: "100%" } : undefined}>
                                                            {title}
                                                        </Typography>
                                                    )}
                                                    {titleAside}
                                                </Box>
                                                {/* The centre slot shares the free space with the title
                                                    elsewhere; on a stock page the title has all of it,
                                                    and this is a gap before the icons. Left empty, it keeps
                                                    only a sliver, so a title that just fits is not cut short. */}
                                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flex: stockPage ? "0 0 auto" : 1, px: stockPage || settingsComponentCentered ? 1 : 0.25 }}>
                                                    {settingsComponentCentered && !isFullscreen && <Box sx={{ display: "flex", alignItems: "center" }}>{settingsComponentCentered}</Box>}
                                                </Box>
                                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, flex: "0 0 auto" }}>
                                                    {headerButton && typeof headerButton.content === "string" && (
                                                        <Button
                                                            variant={headerButton.variant ? headerButton.variant : "text"}
                                                            onClick={headerButton.onClick}
                                                            className={styles["header-button"]}
                                                            sx={{ color: headerButton.variant ? (headerButton.variant === "contained" ? "primary.contrastText" : "#000") : "inherit" }}
                                                        >
                                                            {headerButton.content}
                                                        </Button>
                                                    )}
                                                    {headerButton && typeof headerButton.content !== "string" && !isFullscreen && headerButton.content}
                                                    {settingsComponent && !isFullscreen && <Box sx={{ display: "flex", alignItems: "center" }}>{settingsComponent}</Box>}
                                                    {/* data-tour: generic anchor so guided tours can
                                                        spotlight any page's filter button. */}
                                                    {filters && (
                                                        <Box data-tour="module-filters" sx={{ display: "inline-flex" }}>
                                                            <FiltersBtn filters={filters} />
                                                        </Box>
                                                    )}

                                                    {!isFullscreen && share && (
                                                        <Tooltip title={share.tooltip || "Share"} arrow>
                                                            <IconButton aria-label="share" className={styles.button} onClick={share.onClick} sx={{ mt: 0.3 }}>
                                                                <IosShareOutlinedIcon style={{ color: ortexColor }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {!isFullscreen && feedback && !["ORTEX Visitor"].includes(userData?.subscription_plan) && (
                                                        <Tooltip title="Send feedback" arrow>
                                                            <IconButton aria-label="send feedback" className={styles.button} onClick={() => setFeedbackModal(true)} sx={{ mt: 0.3 }}>
                                                                <FeedbackOutlinedIcon style={{ color: textColor }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {!isFullscreen && (help || hasHelpActions) && (
                                                        <Tooltip title="Help" arrow>
                                                            <IconButton aria-label="help" className={styles.button} onClick={canOpen ? getHelp : undefined}>
                                                                <InfoOutlinedIcon style={{ color: textColor }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    {!isFullscreen && modalComponent && (
                                                        <Box key="settings-box" sx={{ display: "flex" }}>
                                                            <Tooltip title="Settings" arrow>
                                                                <IconButton aria-label="settings" className={styles.button} onClick={() => setSettingsModal(true)} style={{ padding: 0 }}>
                                                                    <SettingsIcon style={{ color: textColor }} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    )}

                                                    {allowFullscreen && !(hydrated && isMobileSafari()) && (
                                                        <Box key="fullscreen-box" sx={{ display: "flex" }}>
                                                            {isFullscreen ? (
                                                                <IconButton aria-label="close-fullscreen" className={styles.button} onClick={toggleFullscreen}>
                                                                    <CloseFullscreenOutlinedIcon style={{ color: textColor }} />
                                                                </IconButton>
                                                            ) : (
                                                                <IconButton aria-label="open-fullscreen" className={styles.button} onClick={toggleFullscreen}>
                                                                    <FullscreenOutlinedIcon style={{ color: textColor }} />
                                                                </IconButton>
                                                            )}
                                                        </Box>
                                                    )}

                                                    {link && (
                                                        <Tooltip title={"Link to " + link} arrow>
                                                            <IconButton aria-label={"Link to " + link} className={styles.button}>
                                                                <Link passHref legacyBehavior href={link}>
                                                                    <OpenInNewIcon style={{ color: textColor }} />
                                                                </Link>
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </Box>
                                        </>
                                    )
                                }
                                avatar={
                                    logoUrl && (
                                        <div className={styles.logo}>
                                            <Image unoptimized src={logoUrl} alt={`logo`} layout="fill" objectFit="contain" />
                                        </div>
                                    )
                                }
                                subheader={subHeader}
                            />
                        )}
                        <CardContent
                            ref={contentRef}
                            className={classNames(
                                ["both", "body"].includes(noPadding?.source) && zeroPadding ? styles["no-padding"] : styles["content-container"],
                                (scrollsWithPage || !contentOverflows) && styles["flows-with-page"],
                                parentObfuscated && "obfuscated"
                            )}
                            sx={{
                                height: `calc(100% - ${module_header_height}px)`,
                                backgroundColor: fullscreen ? (theme === "dark" ? "black" : "white") : "transparent",
                            }}
                        >
                            <div className={styles.child_cardContent}>{children && Children.map(children, (child) => child)}</div>
                        </CardContent>

                        {userData && helpOpened && (
                            <DialogBasic isOpen={helpModal} onClose={setHelpModal}>
                                {hasHelpActions && (
                                    <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                                        {helpActions.map((a) => (
                                            <Button
                                                key={a.label}
                                                size="small"
                                                variant="outlined"
                                                onClick={() => {
                                                    setHelpModal(false);
                                                    a.onClick();
                                                }}
                                            >
                                                {a.label}
                                            </Button>
                                        ))}
                                    </div>
                                )}
                                {help && (
                                    <div className={styles.modal}>{userData.is_staff || userData.is_superuser ? <RichTextEditor markup={content} onSave={saveHelp} /> : <div dangerouslySetInnerHTML={{ __html: inlineFaIcons(content) }}></div>}</div>
                                )}
                            </DialogBasic>
                        )}
                        {settingsOpened && (
                            <DialogBasic isOpen={settingsModal} onClose={setSettingsModal} maxWidth={modalComponentWitdh || "sm"} extraButtons={modalCloseOnExtraClick ? modalExtraButtonsWithClose : modalExtraButtons}>
                                {modalComponent}
                            </DialogBasic>
                        )}
                        {feedbackOpened && <FeedbackForm isOpen={feedbackModal} onClose={setFeedbackModal} moduleId={help?.moduleId} />}
                    </Card>
                </div>
            </StableErrorBoundary>
        </ModuleContext.Provider>
    );
};

export default Module;
